import React, { ChangeEvent, useEffect } from 'react'
import { BpSelect } from '../../forms/select/BpSelect'
import { BpButton } from '../../buttons/bp-button/BpButton'
import { Controller, useForm } from 'react-hook-form'
import { ICompanySettingsFormData } from '../../../../pages/signup/widgets/company-settings-form/CompanySettingsForm'
import { BpInput } from '../../forms/input/BpInput'
import { useTranslation } from 'react-i18next'
import {
  ConfirmContactForm,
  useGetFilterContactMethodQuery,
  usePostUserConfirmContactMutation,
} from '../../../../app/api'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import { selectUser } from '../../../../app/store/reducers/AuthSlice'
import { hideModal, showAlert } from '../../../../app/store/reducers/AppSlice'

interface ConfirmContactModalProps {
  onSuccess?: () => void
}

export const ConfirmContactModal = ({ onSuccess }: ConfirmContactModalProps) => {
  const { t } = useTranslation()
  const user = useAppSelector(selectUser)
  const dispatch = useAppDispatch()

  const [confirmContact] = usePostUserConfirmContactMutation()
  const { data: contactMethodOptions, isLoading: contactMethodOptionsIsLoading } =
    useGetFilterContactMethodQuery()

  const {
    control,
    handleSubmit,
    register,
    formState: { errors, isValid },
    setValue,
    reset,
    watch,
    trigger,
  } = useForm<ICompanySettingsFormData>({
    mode: 'onChange',
    defaultValues: {
      contactMethod: '',
      contactDetails: '',
    },
  })

  const formValues = watch()
  const contactMethod = watch('contactMethod')

  const onSubmit = (values: any) => {
    confirmContact({
      confirmContactForm: {
        contact_method: values.contactMethod as ConfirmContactForm['contact_method'],
        contact_details: values.contactDetails,
      },
    }).then((response: any) => {
      if (response.data?.success) {
        if (onSuccess) {
          onSuccess()
        } else {
          dispatch(
            showAlert({
              message: t('notifications.success'),
              timeout: 2000,
              type: 'success',
            }),
          )
        }
      } else {
        dispatch(
          showAlert({
            message: 'Failed to send request. Try again later.',
            timeout: 2000,
            type: 'error',
          }),
        )
      }
    })
  }

  useEffect(() => {
    if (contactMethodOptions) {
      reset({
        contactMethod: user?.contact_method || 'email',
        contactDetails: user?.contact_method ? user?.contact_details : user?.email || '',
      })
    }
  }, [contactMethodOptions, user])

  useEffect(() => {
    if (contactMethod) {
      void trigger('contactDetails')
    }
  }, [contactMethod, trigger])

  if (contactMethodOptionsIsLoading && !contactMethodOptions) {
    return null
  }

  return (
    <div className={'flex flex-col'}>
      <div className={'pt-[8px]'}>
        <h3
          className={
            'border-b-[3px] border-focus-600/[.03] px-[24px] pb-[8px] text-[20px] font-medium'
          }
        >
          {t('pages.referralProgram.confirmYourContactInfo')}
        </h3>
      </div>
      <form
        className={'flex flex-col gap-[13px] gap-[16px] px-[24px] py-[13px]'}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Controller
          render={({ field: { onChange, ...fieldProps } }) => {
            return (
              <BpSelect
                dropPosition={'auto'}
                {...fieldProps}
                required
                label={t('components.confirmContactModal.preferredContactMethod')}
                options={contactMethodOptions}
                placeholder={'Choose contact method'}
                onChange={(e) => {
                  if (e.target.value !== '' && e.target.value !== formValues.contactMethod) {
                    setValue('contactDetails', '', { shouldValidate: false })
                    onChange(e)

                    setTimeout(() => {
                      void trigger('contactDetails')
                    }, 0)
                  }
                }}
              />
            )
          }}
          control={control}
          name='contactMethod'
        />
        {formValues.contactMethod && (
          <BpInput
            required
            error={errors.contactDetails?.message}
            label={t('components.confirmContactModal.contact.' + formValues.contactMethod)}
            {...register('contactDetails', {
              required: t('fields.validate.required'),
              validate: (value, formValues) => {
                if (!value) return true

                const currentContactMethod = formValues.contactMethod || contactMethod

                switch (currentContactMethod) {
                  case 'email': {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
                    return emailRegex.test(value) || t('fields.validate.invalidEmail')
                  }

                  case 'linkedin': {
                    const linkedinRegex = /^https?:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-]+\/?$/
                    return linkedinRegex.test(value) || t('fields.validate.invalidLinkedIn')
                  }

                  case 'telegram':
                  case 'skype': {
                    const textRegex = /^[a-zA-Z0-9_@.]+$/
                    return textRegex.test(value) || t('fields.validate.invalidUsername')
                  }

                  case 'whatsapp': {
                    const phoneRegex = /^\+?[1-9]\d{1,14}$/
                    return phoneRegex.test(value) || t('fields.validate.invalidPhone')
                  }

                  default:
                    return true
                }
              },
            })}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setValue('contactDetails', e.target.value)

              if (formValues.contactMethod) {
                void trigger('contactDetails')
              }
            }}
          />
        )}
        <p className={'text-[13px] leading-[16px] text-focus-500'}>
          {t('pages.referralProgram.willReachOutToYou')}
        </p>
        <div className={'flex justify-between'}>
          <BpButton className={'uppercase'} disabled={!isValid || !formValues.contactMethod}>
            {t('pages.referralProgram.buttons.confirm')}
          </BpButton>
          <BpButton className={'uppercase'} theme={'outline'} onClick={() => dispatch(hideModal())}>
            {t('pages.referralProgram.buttons.cancel')}
          </BpButton>
        </div>
      </form>
    </div>
  )
}
